import React, { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { setIn } from '@lyearn/core/utils/immutable';
import { useTranslation } from '@lyearn/i18n';
import { Edit } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';
import { Image } from '@/components/Image';
import AddCoverDialog from '@/modules/corporate/components/AddCoverDialog';
import { CoverType } from '@/modules/corporate/components/ImageList/types';
import OptionsListItem from '@/modules/corporate/modules/questions/fields/components/Editor/OptionsListItem';
import { EditorOptionsListItemProps } from '@/modules/corporate/modules/questions/fields/components/Editor/OptionsListItem/types';
import { QuizOptionsActionEnum } from '@/modules/corporate/modules/questions/fields/hooks/useOptionsAction/actions';
import { MediaKindEnum } from '@/types/schema';

import { IMAGE_OPTION_WIDTH, imageStyles } from '../../styles';
import ImagePlaceholder from './ImagePlaceholder';
import { imageOverlayStyles, optionsListItemRootStyles } from './styles';

const CLASSES = { root: cn('px-10 mt-8', optionsListItemRootStyles), optionItem: 'ms-12' };

const EditorOptionsListItem = (props: EditorOptionsListItemProps) => {
  const { className, option, index, onAction } = props;
  const [showDialog, { toggle: toggleDialog }] = useToggle(false);
  const { t } = useTranslation();

  const onSubmit = useCallback(
    (selectedImage?: CoverType) => {
      if (selectedImage?.url) {
        const attachmentPayload = {
          kind: MediaKindEnum.Image,
          url: selectedImage?.url,
          name: selectedImage?.name,
        };

        onAction({
          type: QuizOptionsActionEnum.UpdateOption,
          payload: { index, option: setIn(option, 'attachments', [attachmentPayload]) },
        });
      }
    },
    [index, onAction, option],
  );

  const imageUrl = option?.attachments?.[0]?.url;

  return (
    <Stack className={cn('group group/option mb-32', className)} direction="col">
      {imageUrl ? (
        <Box className="relative" onClick={toggleDialog}>
          <Image
            alt="Upload Image"
            className={cn('border border-divider', imageStyles)}
            height={195}
            objectFit="contain"
            src={imageUrl}
            width={IMAGE_OPTION_WIDTH * 10}
            // since IMAGE_OPTION_WIDTH is in rem
          />
          <Box
            className={cn(
              'absolute top-0 opacity-0 transition hover:opacity-100',
              imageStyles,
              imageOverlayStyles,
            )}>
            <Stack
              alignItems="center"
              className="absolute top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-interactive-blur start-1/2  rtl:translate-x-1/2"
              justifyContent="center">
              <Edit className="h-20 w-20" fillColorToken="interactive-white" />
            </Stack>
          </Box>
        </Box>
      ) : (
        <ImagePlaceholder className={imageStyles} onClick={toggleDialog} />
      )}
      <OptionsListItem {...props} classes={CLASSES} />
      {showDialog ? (
        <AddCoverDialog
          i18n={{
            headerTitle: t('Common:Image.AddAnImage'),
            actionText: t('Common:Actions.Confirm'),
          }}
          onClose={toggleDialog}
          onSubmit={onSubmit}
        />
      ) : null}
    </Stack>
  );
};

export default EditorOptionsListItem;
